import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { setToken } from '@/lib/authToken';

/**
 * Receives the redirect from the backend after Google OAuth.
 * The backend appends the session token to the URL fragment
 * (e.g. `/login/callback#token=<sig>`) — the fragment never hits the
 * server, so the token stays out of access logs.
 */
export function LoginCallbackRoute() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fragment = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(fragment);
    const token = params.get('token');
    if (!token) {
      setError('Login callback did not include a session token.');
      return;
    }
    setToken(token);
    // Replace history entry so the token is out of the URL bar + back button.
    window.history.replaceState(null, '', '/');
    nav('/', { replace: true });
  }, [nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="text-center">
        {error ? (
          <div className="max-w-sm">
            <AlertCircle size={20} className="mx-auto text-[color:var(--danger)] mb-2" />
            <div className="text-sm font-semibold text-fg">{error}</div>
            <a href="/login" className="text-xs text-accent hover:underline mt-3 inline-block">
              Back to sign-in →
            </a>
          </div>
        ) : (
          <div className="text-sm text-fg-muted inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            Signing you in…
          </div>
        )}
      </div>
    </div>
  );
}
